import { useState } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as rootRoute } from '../../__root';
import { Layout } from '@/client/layouts/layout';
import { API_BASE_URL } from '@/client/config';
import { getMonthName } from '@/client/lib/date';
import { downloadCSV } from '@/client/lib/csv';
import { Download, FileSpreadsheet, Radio } from 'lucide-react';
import type {
  SimulcastReportResponse,
  AllClientsSimulcastResponse,
} from '@shared/types';
import type { ClientResponse } from '@shared/types';

import { Badge } from '@/client/components/ui/badge';
import { Button } from '@/client/components/ui/button';
import { Checkbox } from '@/client/components/ui/checkbox';
import { Label } from '@/client/components/ui/label';
import { CacheTimestamp } from '@/client/components/cache-timestamp';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { ClientSelector } from '@/client/components/client-selector';
import { DateRangePicker } from '@/client/components/date-range-picker';
import { ResetFiltersButton } from '@/client/components/reset-filters-button';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'events/reports/simulcast',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: SimulcastReportsComponent,
});

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function minutesToHours(minutes: number): string {
  const hours = minutes / 60;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(hours);
}

function SimulcastReportsComponent() {
  const { getAccessTokenSilently } = useAuth0();

  // Date range state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [quickRange, setQuickRange] = useState('');

  // Client selection state
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientResponse | null>(null);
  const [allClients, setAllClients] = useState(false);

  // Report state - single client
  const [singleClientData, setSingleClientData] = useState<SimulcastReportResponse | null>(null);

  // Report state - all clients
  const [allClientsData, setAllClientsData] = useState<AllClientsSimulcastResponse | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmPending, setConfirmPending] = useState(false);

  // Can generate report if dates are set AND (allClients OR specific client selected)
  const canGenerateReport = startDate && endDate && (allClients || selectedClientId);

  const handleClientChange = (clientId: string, client: ClientResponse | null) => {
    setSelectedClientId(clientId);
    setSelectedClient(client);
    setSingleClientData(null);
    setAllClientsData(null);
    setConfirmPending(false);
  };

  const handleAllClientsChange = (checked: boolean) => {
    setAllClients(checked);
    setSingleClientData(null);
    setAllClientsData(null);
    setConfirmPending(false);
  };

  const handleGenerateReport = async () => {
    if (!canGenerateReport) return;

    if (!confirmPending) {
      setConfirmPending(true);
      return;
    }

    setConfirmPending(false);
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      if (allClients) {
        const response = await fetch(
          `${API_BASE_URL}/api/events/reports/simulcast/all?startDate=${startDate}&endDate=${endDate}`,
          { headers }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch simulcast data: ${response.statusText}`);
        }

        const data: AllClientsSimulcastResponse = await response.json();
        setAllClientsData(data);
        setSingleClientData(null);
      } else {
        const response = await fetch(
          `${API_BASE_URL}/api/events/reports/simulcast?clientID=${selectedClientId}&startDate=${startDate}&endDate=${endDate}`,
          { headers }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch simulcast data: ${response.statusText}`);
        }

        const data: SimulcastReportResponse = await response.json();
        setSingleClientData(data);
        setAllClientsData(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // CSV Export - Single Client
  const handleExportSingleClient = () => {
    if (!singleClientData) return;

    const headers = [
      'Year',
      'Month',
      'Events with Simulcast',
      'Total Targets',
      'Event Duration (min)',
      'Event Duration (hrs)',
      'Simulcast Duration (min)',
      'Simulcast Duration (hrs)',
    ];
    const rows: string[][] = singleClientData.monthly.map(row => [
      row.year.toString(),
      getMonthName(row.month),
      row.eventsWithSimulcast.toString(),
      row.totalTargets.toString(),
      row.totalDurationMinutes.toString(),
      (row.totalDurationMinutes / 60).toFixed(2),
      row.simulcastMinutes.toString(),
      (row.simulcastMinutes / 60).toFixed(2),
    ]);

    rows.push([
      'TOTAL',
      '',
      singleClientData.totals.eventsWithSimulcast.toString(),
      singleClientData.totals.totalTargets.toString(),
      singleClientData.totals.totalDurationMinutes.toString(),
      (singleClientData.totals.totalDurationMinutes / 60).toFixed(2),
      singleClientData.totals.simulcastMinutes.toString(),
      (singleClientData.totals.simulcastMinutes / 60).toFixed(2),
    ]);

    const clientName = selectedClient?.name.replace(/[^a-zA-Z0-9]/g, '_') || 'client';
    downloadCSV(
      `simulcast_${clientName}_${singleClientData.dateRange.start}_to_${singleClientData.dateRange.end}.csv`,
      headers,
      rows
    );
  };

  // CSV Export - All Clients
  const handleExportAllClients = () => {
    if (!allClientsData) return;

    const headers = [
      'Client ID',
      'Client Name',
      'Events with Simulcast',
      'Total Targets',
      'Event Duration (hrs)',
      'Simulcast Duration (hrs)',
    ];
    const rows: string[][] = allClientsData.clients.map(client => [
      client.clientID,
      client.clientName,
      client.eventsWithSimulcast.toString(),
      client.totalTargets.toString(),
      client.totalDurationHours.toFixed(2),
      client.simulcastHours.toFixed(2),
    ]);

    rows.push([
      'GRAND TOTAL',
      `(${allClientsData.grandTotals.clientCount} clients)`,
      allClientsData.grandTotals.eventsWithSimulcast.toString(),
      allClientsData.grandTotals.totalTargets.toString(),
      allClientsData.grandTotals.totalDurationHours.toFixed(2),
      allClientsData.grandTotals.simulcastHours.toFixed(2),
    ]);

    downloadCSV(
      `simulcast_all_clients_${allClientsData.dateRange.start}_to_${allClientsData.dateRange.end}.csv`,
      headers,
      rows
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Radio className="h-6 w-6" />
            <h3 className="text-2xl font-semibold">Simulcast Reports</h3>
          </div>
          <CacheTimestamp
            cachedAt={
              singleClientData?.cacheInfo?.cachedAt ??
              allClientsData?.cacheInfo?.cachedAt ??
              null
            }
          />
        </div>

        <p className="text-sm text-muted-foreground">
          Track events with external stream targets (YouTube, Facebook, Twitch, etc.) and their simulcast durations.
        </p>

        {/* Controls Section */}
        <div className="border rounded-lg p-4 space-y-4 bg-card">
          {/* Date Range Section */}
          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Date Range</span>
            <div className="flex flex-wrap gap-4 items-end">
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                quickRange={quickRange}
                onQuickRangeChange={setQuickRange}
                showToday={false}
                onDateChange={() => setConfirmPending(false)}
              />
            </div>
          </div>

          {/* Client & Options Section */}
          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Client</span>
            <div className="flex flex-wrap gap-x-4 gap-y-3 items-center">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="all-clients"
                  checked={allClients}
                  onCheckedChange={(checked) => handleAllClientsChange(checked === true)}
                />
                <Label htmlFor="all-clients" className="cursor-pointer">
                  All Clients
                </Label>
              </div>

              <span className="text-muted-foreground">or</span>

              <ClientSelector
                value={selectedClientId}
                onChange={handleClientChange}
                className="min-w-[250px]"
                label=""
                disabled={allClients}
              />

              <Button
                onClick={handleGenerateReport}
                disabled={!canGenerateReport || loading}
                variant={confirmPending ? 'destructive' : 'default'}
                className="min-w-[175px]"
              >
                {loading ? (
                  'Loading...'
                ) : confirmPending ? (
                  'Click to Confirm'
                ) : (
                  <>
                    <FileSpreadsheet className="mr-2 h-4 w-4" />
                    Generate Report
                  </>
                )}
              </Button>

              <ResetFiltersButton
                routePath="/events/reports/simulcast"
                defaultValues={{ clientID: undefined, startDate: undefined, endDate: undefined, quickRange: undefined, allClients: false }}
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded">
            <p className="m-0 text-red-800 font-medium">Error: {error}</p>
          </div>
        )}

        {/* Results Section - Single Client */}
        {singleClientData && (
          <div className="space-y-8">
            <div className="text-sm text-muted-foreground">
              Report for Client <Badge variant="outline" className="text-xs font-mono">{singleClientData.clientID}</Badge> from <Badge variant="outline" className="text-xs font-mono">{startDate} to {endDate}</Badge>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold">Simulcast Usage by Month</h4>
                <Button variant="outline" size="sm" onClick={handleExportSingleClient}>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Year</TableHead>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Events</TableHead>
                      <TableHead className="text-right">Targets</TableHead>
                      <TableHead className="text-right">Event Duration (min)</TableHead>
                      <TableHead className="text-right">Event Duration (hrs)</TableHead>
                      <TableHead className="text-right">Simulcast Duration (min)</TableHead>
                      <TableHead className="text-right">Simulcast Duration (hrs)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {singleClientData.monthly.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground">
                          No simulcast data for this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {singleClientData.monthly.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{row.year}</TableCell>
                            <TableCell>{getMonthName(row.month)}</TableCell>
                            <TableCell className="text-right">{formatNumber(row.eventsWithSimulcast)}</TableCell>
                            <TableCell className="text-right">{formatNumber(row.totalTargets)}</TableCell>
                            <TableCell className="text-right">{formatNumber(row.totalDurationMinutes)}</TableCell>
                            <TableCell className="text-right">{minutesToHours(row.totalDurationMinutes)}</TableCell>
                            <TableCell className="text-right">{formatNumber(row.simulcastMinutes)}</TableCell>
                            <TableCell className="text-right">{minutesToHours(row.simulcastMinutes)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50 font-semibold">
                          <TableCell colSpan={2}>Total</TableCell>
                          <TableCell className="text-right">
                            {formatNumber(singleClientData.totals.eventsWithSimulcast)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(singleClientData.totals.totalTargets)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(singleClientData.totals.totalDurationMinutes)}
                          </TableCell>
                          <TableCell className="text-right">
                            {minutesToHours(singleClientData.totals.totalDurationMinutes)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(singleClientData.totals.simulcastMinutes)}
                          </TableCell>
                          <TableCell className="text-right">
                            {minutesToHours(singleClientData.totals.simulcastMinutes)}
                          </TableCell>
                        </TableRow>
                        <TableRow className="bg-muted/30 text-sm">
                          <TableCell colSpan={3} className="text-right">
                            Avg Targets/Event:
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {singleClientData.totals.avgTargetsPerEvent.toFixed(2)}
                          </TableCell>
                          <TableCell colSpan={2} className="text-right">
                            Avg Duration/Event:
                          </TableCell>
                          <TableCell colSpan={2} className="text-right font-medium">
                            {minutesToHours(singleClientData.totals.avgDurationMinutes)} hrs
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}

        {/* Results Section - All Clients */}
        {allClientsData && (
          <div className="space-y-8">
            <div className="text-sm text-muted-foreground">
              All Clients Report from {startDate} to {endDate}
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold">Simulcast Usage - All Clients</h4>
                <Button variant="outline" size="sm" onClick={handleExportAllClients}>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client ID</TableHead>
                      <TableHead>Client Name</TableHead>
                      <TableHead className="text-right">Events</TableHead>
                      <TableHead className="text-right">Targets</TableHead>
                      <TableHead className="text-right">Event Duration (hrs)</TableHead>
                      <TableHead className="text-right">Simulcast Duration (hrs)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allClientsData.clients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          No simulcast data for this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {allClientsData.clients.map((client) => (
                          <TableRow key={client.clientID}>
                            <TableCell>{client.clientID}</TableCell>
                            <TableCell>{client.clientName}</TableCell>
                            <TableCell className="text-right">{formatNumber(client.eventsWithSimulcast)}</TableCell>
                            <TableCell className="text-right">{formatNumber(client.totalTargets)}</TableCell>
                            <TableCell className="text-right">{client.totalDurationHours.toFixed(2)}</TableCell>
                            <TableCell className="text-right">{client.simulcastHours.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50 font-semibold">
                          <TableCell>Grand Total</TableCell>
                          <TableCell className="text-muted-foreground">
                            ({allClientsData.grandTotals.clientCount} clients)
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(allClientsData.grandTotals.eventsWithSimulcast)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(allClientsData.grandTotals.totalTargets)}
                          </TableCell>
                          <TableCell className="text-right">
                            {allClientsData.grandTotals.totalDurationHours.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right">
                            {allClientsData.grandTotals.simulcastHours.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
